import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { purchaseAPI, accountingAPI, rawMaterialAPI, warehouseAPI, prepaymentAPI } from '../utils/api';
import PrepaymentPicker from '../components/common/PrepaymentPicker';
import { Truck, Plus, Trash2, X, ChevronDown, ChevronUp, Wallet, CheckCircle2, AlertTriangle, RotateCcw, Search, Ship } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import Modal from '../components/common/Modal';
import MoneyModal from '../components/common/MoneyModal';
import { localName, fmt, money, cur, apiError, today } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';
import useUrlQuery from '../utils/useUrlQuery';

const emptyItem = () => ({ raw_material_id: '', quantity: '', unit_price: '' });

export default function Purchases() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user, can } = useAuthStore();
  const canEdit = can('purchases.edit');
  const isAdmin = can('records.delete');

  const [rows, setRows] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [mats, setMats] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  useUrlQuery(v => { setSearch(v); setFilter(''); });
  const [modal, setModal] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => Promise.all([purchaseAPI.list(), accountingAPI.suppliers(), rawMaterialAPI.list(), warehouseAPI.list(), accountingAPI.currencies(), accountingAPI.accounts()])
    .then(([p, s, m, w, c, a]) => { setRows(p.data); setSuppliers(s.data); setMats(m.data); setWarehouses(w.data); setCurrencies(c.data); setAccounts(a.data); })
    .catch(() => toast.error(t('error')));
  useEffect(() => { load(); }, []); // eslint-disable-line

  // v3.9: /purchases?from_prepayment=<id> - goods of a supplier contract arrived: prefill the remaining lines
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const pid = params.get('from_prepayment');
    if (!pid || !warehouses.length) return;
    params.delete('from_prepayment'); setParams(params, { replace: true });
    Promise.all([prepaymentAPI.get(pid), purchaseAPI.nextNumber().catch(() => ({ data: {} }))]).then(([r, n]) => {
      const pp = r.data;
      const lines = pp.lines.filter(l => l.item_type === 'raw' && l.remaining > 0);
      setForm({ purchase_number: n.data.purchase_number || '', purchase_date: today(), supplier_id: pp.party_id, warehouse_id: warehouses[0]?.id || '',
        currency_code: pp.currency_code, per_usd: rateOf(pp.currency_code) || 1, payment_type: 'credit', due_date: '', account_id: '', notes: pp.number,
        via_shipment: false, prepayment_id: pp.id, prepayment_amount: '',
        items: lines.length ? lines.map(l => ({ raw_material_id: l.raw_material_id, quantity: l.remaining, unit_price: l.unit_price ?? '' })) : [emptyItem()] });
      setModal(true);
    }).catch(() => toast.error(t('error')));
  }, [params, warehouses]); // eslint-disable-line

  const rateOf = code => currencies.find(c => c.code === code)?.per_usd || '';
  const openNew = async () => {
    let num = '';
    try { num = (await purchaseAPI.nextNumber()).data.purchase_number; } catch { /* ignore */ }
    setForm({ purchase_number: num, purchase_date: today(), supplier_id: '', warehouse_id: warehouses[0]?.id || '', currency_code: 'USD', per_usd: 1,
      payment_type: 'credit', due_date: '', account_id: '', notes: '', via_shipment: false, prepayment_id: '', prepayment_amount: '', items: [emptyItem()] });
    setModal(true);
  };
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v, ...(k === 'currency_code' ? { per_usd: rateOf(v) } : {}) }));
  const updItem = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const total = form ? form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0) : 0;

  const save = async () => {
    if (!form.purchase_number.trim() || !form.warehouse_id) return toast.error(t('fill_required'));
    if (form.items.some(i => !i.raw_material_id || !(parseFloat(i.quantity) > 0) || i.unit_price === '')) return toast.error(t('fill_all_items'));
    if (!(parseFloat(form.per_usd) > 0)) return toast.error(t('rate_missing'));
    setSaving(true);
    try {
      await purchaseAPI.create({ ...form, supplier_id: form.supplier_id || null, account_id: form.payment_type === 'cash' ? form.account_id || null : null,
        due_date: form.due_date || null, per_usd: parseFloat(form.per_usd),
        prepayment_id: form.payment_type === 'credit' && form.prepayment_id ? form.prepayment_id : null,
        prepayment_amount: form.prepayment_amount ? parseFloat(form.prepayment_amount) : null,
        items: form.items.map(i => ({ raw_material_id: i.raw_material_id, quantity: parseFloat(i.quantity), unit_price: parseFloat(i.unit_price) })) });
      toast.success(form.via_shipment ? t('sh_created_from_purchase') : t('saved')); setModal(false); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const act = (fn, msg) => async id => {
    if (msg && !window.confirm(msg)) return;
    try { await fn(id); toast.success(t('saved')); load(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const del = act(purchaseAPI.delete, t('confirm_delete'));
  const markPaid = act(purchaseAPI.markPaid, t('confirm_mark_paid'));
  const markUnpaid = act(purchaseAPI.markUnpaid, t('confirm_mark_unpaid'));

  const q = search.trim().toLowerCase();
  const list = (rows || []).filter(r => (!filter || r.status === filter) && (!q || [r.purchase_number, r.supplier, r.notes].some(v => (v || '').toLowerCase().includes(q))));
  const owed = (rows || []).reduce((s, r) => s + r.remaining_usd, 0);

  return (
    <div className="space-y-5">
      <SectionHero title={t('purchases')} subtitle={<>{(rows || []).length} {t('purchases')} · {t('payable')}: {money(owed)}</>}>
        {canEdit && <button className="btn btn-primary" onClick={openNew}><Plus size={16} /> {t('new_purchase')}</button>}
      </SectionHero>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px' }}>
            <Search size={15} style={{ color: 'var(--text-muted)' }} />
            <input className="input" style={{ border: 'none', background: 'transparent', padding: 0 }} placeholder={t('search') + '...'} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select" style={{ width: 170 }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">{t('all')}</option><option value="pending">{t('unpaid')}</option><option value="paid">{t('paid')}</option>
          </select>
        </div>
        {!rows ? <div className="loading-center"><div className="spinner" /></div> : list.length === 0 ? <div className="empty-state"><Truck size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th style={{ width: 32 }}></th><th>{t('purchase_number')}</th><th>{t('date')}</th><th>{t('supplier')}</th><th>{t('amount')}</th><th>{t('status')}</th>{canEdit && <th>{t('actions')}</th>}</tr></thead>
            <tbody>{list.map(p => (
              <React.Fragment key={p.id}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => ({ ...e, [p.id]: !e[p.id] }))}>
                  <td>{expanded[p.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                  <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{p.purchase_number}
                    {p.shipment_id && <Link to={`/shipments?open=${p.shipment_id}`} onClick={e => e.stopPropagation()} className={`transit-chip ${p.shipment_status}`}><Ship size={11} /> {t('sh_status_' + p.shipment_status)}</Link>}</td>
                  <td>{p.purchase_date}</td>
                  <td>{p.supplier || '—'}</td>
                  <td><strong style={{ color: 'var(--text-primary)' }}>{cur(p.total_amount, p.currency_code)}</strong>{p.currency_code !== 'USD' && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>≈ {money(p.total_usd)}</div>}</td>
                  <td>
                    {p.status === 'paid' ? <span className="badge badge-green"><CheckCircle2 size={10} /> {t('paid')}</span>
                      : p.is_overdue ? <span className="badge badge-red"><AlertTriangle size={10} /> {t('overdue')}</span>
                        : <span className="badge badge-yellow">{t('unpaid')}</span>}
                    {p.status !== 'paid' && <div className="text-xs" style={{ color: '#fbbf24' }}>{t('remaining')}: {cur(p.remaining, p.currency_code)}</div>}
                  </td>
                  {canEdit && (
                    <td onClick={e => e.stopPropagation()}><div className="flex gap-1">
                      {p.status !== 'paid' && <button className="btn btn-sm" style={{ background: 'var(--green-dim)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }} onClick={() => setPayFor(p)}><Wallet size={13} /> {t('pay')}</button>}
                      {p.status !== 'paid' && <button className="btn btn-ghost btn-sm btn-icon" title={t('mark_paid')} onClick={() => markPaid(p.id)}><CheckCircle2 size={13} /></button>}
                      {p.status === 'paid' && p.payment_type === 'credit' && <button className="btn btn-ghost btn-sm btn-icon" title={t('mark_unpaid')} onClick={() => markUnpaid(p.id)}><RotateCcw size={13} /></button>}
                      {isAdmin && <button className="btn btn-danger btn-sm btn-icon" onClick={() => del(p.id)}><Trash2 size={13} /></button>}
                    </div></td>
                  )}
                </tr>
                {expanded[p.id] && (
                  <tr><td colSpan={canEdit ? 7 : 6} style={{ background: 'rgba(0,0,0,0.2)', padding: '12px 20px' }}>
                    <div className="flex flex-wrap gap-3 text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                      <span>{t('warehouse')}: <strong>{p.warehouse}</strong></span>
                      <span>{t('payment_type')}: <strong>{t('purchase_pay_' + p.payment_type)}</strong></span>
                      {p.currency_code !== 'USD' && <span>{t('rate_per_usd')}: {fmt(p.per_usd)}</span>}
                      {p.due_date && <span>{t('due_date')}: {p.due_date}</span>}
                      <span>{t('paid')}: {money(p.paid_usd)}</span>
                    </div>
                    {p.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-xs" style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: 8, marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ color: 'var(--text-primary)' }}>{localName(it, lang)}</span>
                        <span>{fmt(it.quantity)} {it.unit} × {cur(it.unit_price, p.currency_code)} = <strong style={{ color: 'var(--green)' }}>{cur(it.total_price, p.currency_code)}</strong></span>
                      </div>
                    ))}
                    {p.notes && <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>{p.notes}</p>}
                  </td></tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && form && (
        <Modal title={t('new_purchase')} size="modal-lg" onClose={() => setModal(false)}
          footer={<><button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('purchase_number')} *</label><input className="input" value={form.purchase_number} onChange={e => upd('purchase_number', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={form.purchase_date} onChange={e => upd('purchase_date', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('supplier')}</label>
              <select className="select" value={form.supplier_id} onChange={e => upd('supplier_id', e.target.value)}>
                <option value="">{t('no_supplier')}</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">{form.via_shipment ? t('sh_destination_wh') : t('receiving_warehouse')} *</label>
              <select className="select" value={form.warehouse_id} onChange={e => upd('warehouse_id', e.target.value)}>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('currency')}</label>
              <select className="select" value={form.currency_code} onChange={e => upd('currency_code', e.target.value)}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">{t('rate_per_usd')}</label>
              <input className="input" type="number" min="0" step="any" disabled={form.currency_code === 'USD'} value={form.per_usd} onChange={e => upd('per_usd', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('payment_type')}</label>
              <select className="select" value={form.payment_type} onChange={e => upd('payment_type', e.target.value)}>
                <option value="credit">{t('purchase_pay_credit')}</option><option value="cash">{t('purchase_pay_cash')}</option>
              </select></div>
            {form.payment_type === 'credit'
              ? <div className="form-group"><label className="form-label">{t('due_date')}</label><input className="input" type="date" value={form.due_date} onChange={e => upd('due_date', e.target.value)} /></div>
              : <div className="form-group"><label className="form-label">{t('paid_from_account')}</label>
                  <select className="select" value={form.account_id} onChange={e => upd('account_id', e.target.value)}>
                    <option value="">{t('no_account_record')}</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
                  </select></div>}
          </div>
          <PrepaymentPicker kind="supplier" partyId={form.supplier_id} value={{ prepayment_id: form.prepayment_id, prepayment_amount: form.prepayment_amount }}
            onChange={v => setForm(f => ({ ...f, ...v }))} onNeedCredit={() => setForm(f => ({ ...f, payment_type: 'credit' }))} />
          <label className={`via-ship ${form.via_shipment ? 'on' : ''}`}>
            <input type="checkbox" checked={form.via_shipment} onChange={e => upd('via_shipment', e.target.checked)} />
            <Ship size={18} />
            <span><b>{t('sh_via_shipment')}</b><small>{t('sh_via_shipment_hint')}</small></span>
          </label>
          <div className="divider" />
          <div className="flex items-center justify-between mb-3">
            <p className="form-label" style={{ margin: 0 }}>{t('items')}</p>
            <button className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))}><Plus size={13} /> {t('add')}</button>
          </div>
          {form.items.map((it, i) => (
            <div key={i} className="flex gap-2 flex-wrap items-center mb-2">
              <select className="select" value={it.raw_material_id} onChange={e => updItem(i, 'raw_material_id', e.target.value)} style={{ flex: '2 1 180px' }}>
                <option value="">{t('select_material')}</option>{mats.map(m => <option key={m.id} value={m.id}>{localName(m, lang)} ({m.unit})</option>)}
              </select>
              <input className="input" type="number" min="0" step="any" placeholder={t('quantity')} value={it.quantity} onChange={e => updItem(i, 'quantity', e.target.value)} style={{ flex: '1 1 90px' }} />
              <input className="input" type="number" min="0" step="any" placeholder={`${t('unit_price')} (${form.currency_code})`} value={it.unit_price} onChange={e => updItem(i, 'unit_price', e.target.value)} style={{ flex: '1 1 120px' }} />
              {form.items.length > 1 && <button className="btn btn-ghost btn-icon" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}><X size={14} /></button>}
            </div>
          ))}
          {total > 0 && <div className="flex justify-end gap-2 mt-2">
            <span className="badge badge-green" style={{ fontSize: 13, padding: '5px 14px' }}>{t('total')}: {cur(total, form.currency_code)}</span>
            {form.currency_code !== 'USD' && parseFloat(form.per_usd) > 0 && <span className="badge badge-blue" style={{ fontSize: 13, padding: '5px 14px' }}>≈ {money(total / parseFloat(form.per_usd))}</span>}
          </div>}
          <div className="divider" />
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('notes')}</label><textarea className="input" style={{ minHeight: 56 }} value={form.notes} onChange={e => upd('notes', e.target.value)} /></div>
        </Modal>
      )}

      {payFor && (
        <MoneyModal title={`${t('pay')} — ${payFor.purchase_number}`} suggestedUsd={payFor.remaining_usd} submitLabel={t('pay')}
          onSubmit={payload => purchaseAPI.pay(payFor.id, payload)} onClose={changed => { setPayFor(null); if (changed) load(); }} />
      )}
    </div>
  );
}
